"use client";

import { Minus, Plus, Trophy, X } from "lucide-react";
import {
  UserTagPicker,
  type UserTagSuggestion,
} from "@/components/posts/user-tag-picker";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";

// ──────────────────────────────────────────────────────────────────────────────
// TYPES
// ──────────────────────────────────────────────────────────────────────────────

export type Participant = {
  id: string;
  displayName: string;
  avatarUrl?: string | null;
  handle?: string;
};

export type TeamPosition = "your-team" | "opponent-team";

// ──────────────────────────────────────────────────────────────────────────────
// SCORE COUNTER COMPONENT
// ──────────────────────────────────────────────────────────────────────────────

type ScoreCounterProps = {
  label: string;
  value: number;
  onChange: (value: number) => void;
  min?: number;
  max?: number;
  isWinner?: boolean;
  variant?: "brand" | "opponent";
  disabled?: boolean;
};

export function ScoreCounter({
  label,
  value,
  onChange,
  min = 0,
  max = 99,
  isWinner = false,
  variant = "brand",
  disabled = false,
}: ScoreCounterProps) {
  return (
    <div className="flex flex-col items-center gap-3">
      <span
        className={cn(
          "text-xs font-semibold tracking-wider uppercase",
          variant === "brand"
            ? "text-brand dark:text-violet-300"
            : "text-text-secondary",
        )}
      >
        {label}
      </span>
      <div className="flex items-center gap-2">
        <button
          type="button"
          aria-label={`${label}: decrease score`}
          onClick={() => onChange(Math.max(min, value - 1))}
          disabled={disabled || value === min}
          className={cn(
            "flex h-10 w-10 items-center justify-center rounded-full bg-surface-alt transition-all active:scale-90",
            "text-text-secondary hover:bg-surface-elevated hover:text-text-primary",
            "disabled:opacity-30 disabled:active:scale-100",
          )}
        >
          <Minus className="h-5 w-5" />
        </button>
        <div
          className={cn(
            "flex h-16 w-16 items-center justify-center rounded-2xl bg-surface shadow-sm ring-1 ring-border/20",
            "text-4xl font-bold tracking-tight tabular-nums",
            isWinner
              ? variant === "brand"
                ? "bg-brand/5 text-brand ring-brand/20"
                : "bg-error/5 text-error ring-error/20"
              : cn("text-text-primary", disabled && "opacity-50"),
          )}
        >
          {value}
        </div>
        <button
          type="button"
          aria-label={`${label}: increase score`}
          onClick={() => onChange(Math.min(max, value + 1))}
          disabled={disabled || value === max}
          className={cn(
            "flex h-10 w-10 items-center justify-center rounded-full bg-surface-alt transition-all active:scale-90",
            "text-text-secondary hover:bg-surface-elevated hover:text-text-primary",
            "disabled:opacity-30 disabled:active:scale-100",
          )}
        >
          <Plus className="h-5 w-5" />
        </button>
      </div>
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────────────
// SCORE INPUT COMPONENT (For breakdowns)
// ──────────────────────────────────────────────────────────────────────────────

export function ScoreInput({
  className,
  ...props
}: React.ComponentProps<typeof Input>) {
  return (
    <Input
      type="number"
      inputMode="numeric"
      {...props}
      className={cn(
        "h-10 w-14 border-transparent bg-surface-elevated text-center text-lg font-semibold tabular-nums shadow-sm ring-1 ring-border/10 transition-all focus:border-brand/20 focus:bg-surface focus:ring-2 focus:ring-brand/20",
        "placeholder:text-text-tertiary",
        className,
      )}
    />
  );
}

// ──────────────────────────────────────────────────────────────────────────────
// MATCH RESULT INDICATOR
// ──────────────────────────────────────────────────────────────────────────────

type MatchResultProps = {
  yourScore: number;
  opponentScore: number;
};

export function MatchResult({ yourScore, opponentScore }: MatchResultProps) {
  const yourWon = yourScore > opponentScore;
  const opponentWon = opponentScore > yourScore;
  const isDraw = yourScore === opponentScore && yourScore > 0;

  if (!yourWon && !opponentWon && !isDraw) return null;

  return (
    <div
      className={cn(
        "flex items-center justify-center gap-2 rounded-lg py-2 text-sm font-medium",
        isDraw
          ? "bg-text-secondary/10 text-text-secondary"
          : yourWon
            ? "bg-brand/10 text-brand"
            : "bg-error/10 text-error",
      )}
    >
      <Trophy className="h-4 w-4" />
      {isDraw ? "Draw" : yourWon ? "You won!" : "Opponent won"}
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────────────
// TEAM CARD COMPONENT
// ──────────────────────────────────────────────────────────────────────────────

type TeamCardProps = {
  label: string;
  isYourTeam: boolean;
  players: Participant[];
  maxPlayers: number;
  currentUserId: string;
  selectedUserIds: Set<string>;
  onAddPlayer: (suggestion: UserTagSuggestion) => void;
  onRemovePlayer: (userId: string) => void;
  highlight?: boolean;
  isComplete?: boolean;
};

export function TeamCard({
  label,
  isYourTeam,
  players,
  maxPlayers,
  currentUserId,
  selectedUserIds,
  onAddPlayer,
  onRemovePlayer,
  highlight,
}: TeamCardProps) {
  const canAdd = players.length < maxPlayers;

  return (
    <div
      className={cn(
        "rounded-xl border-2 p-3 transition-colors",
        highlight
          ? isYourTeam
            ? "border-brand bg-brand/5"
            : "border-error bg-error/5"
          : "border-border bg-surface-alt/50",
      )}
    >
      <div className="mb-2 flex items-center justify-between">
        <span
          className={cn(
            "text-sm font-semibold",
            isYourTeam ? "text-brand" : "text-text-secondary",
          )}
        >
          {label}
        </span>
        <span className="text-xs text-text-secondary">
          {players.length}/{maxPlayers}
        </span>
      </div>

      <div className="space-y-2">
        {players.map((player) => (
          <div
            key={player.id}
            className="flex items-center gap-2 rounded-lg bg-surface/80 px-2 py-1.5"
          >
            <Avatar className="h-7 w-7 border border-border">
              <AvatarImage src={player.avatarUrl ?? undefined} />
              <AvatarFallback className="text-2xs">
                {player.displayName.slice(0, 2).toUpperCase()}
              </AvatarFallback>
            </Avatar>
            <span className="flex-1 truncate text-sm text-text-primary">
              {player.displayName}
              {player.id === currentUserId && (
                <span className="ml-1 text-xs text-brand">(You)</span>
              )}
            </span>
            {player.id !== currentUserId && (
              <button
                type="button"
                onClick={() => onRemovePlayer(player.id)}
                className="rounded-full p-0.5 hover:bg-error/20"
              >
                <X className="h-3.5 w-3.5 text-error" />
              </button>
            )}
          </div>
        ))}

        {canAdd && (
          <UserTagPicker
            onSelect={onAddPlayer}
            selectedUserIds={selectedUserIds}
            currentUserId={currentUserId}
            buttonLabel={
              <span className="flex items-center gap-1">
                <Plus className="h-3 w-3" />
                Add
              </span>
            }
            className="h-8 w-full justify-center"
          />
        )}
      </div>
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────────────
// PARTICIPANT LIST (Non-team based activities)
// ──────────────────────────────────────────────────────────────────────────────

type ParticipantListProps = {
  label?: string;
  participants: Participant[];
  maxParticipants?: number;
  currentUserId: string;
  onAddParticipant: (suggestion: UserTagSuggestion) => void;
  onRemoveParticipant: (userId: string) => void;
  isOptional?: boolean;
};

export function ParticipantList({
  label = "With",
  participants,
  maxParticipants = 10,
  currentUserId,
  onAddParticipant,
  onRemoveParticipant,
  isOptional = false,
}: ParticipantListProps) {
  const selectedUserIds = new Set(participants.map((p) => p.id));
  const canAdd = participants.length < maxParticipants;
  const otherParticipants = participants.filter((p) => p.id !== currentUserId);

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <span className="flex items-center gap-2 text-sm font-medium text-text-primary">
          {label}
          {isOptional && (
            <span className="rounded bg-surface-alt px-1.5 py-0.5 text-xs font-normal text-text-secondary">
              Optional
            </span>
          )}
        </span>
        {otherParticipants.length > 0 && (
          <span className="text-xs text-text-secondary">
            +{otherParticipants.length} tagged
          </span>
        )}
      </div>

      <div className="flex flex-wrap gap-2">
        {otherParticipants.map((participant) => (
          <div
            key={participant.id}
            className="flex items-center gap-2 rounded-full border border-border bg-surface-alt px-3 py-1.5"
          >
            <Avatar className="h-6 w-6 border border-border">
              <AvatarImage src={participant.avatarUrl ?? undefined} />
              <AvatarFallback className="text-2xs">
                {participant.displayName.slice(0, 2).toUpperCase()}
              </AvatarFallback>
            </Avatar>
            <span className="text-sm text-text-primary">
              {participant.displayName.split(" ")[0]}
            </span>
            <button
              type="button"
              onClick={() => onRemoveParticipant(participant.id)}
              className="rounded-full p-0.5 hover:bg-error/20"
            >
              <X className="h-3 w-3 text-error" />
            </button>
          </div>
        ))}

        {canAdd && (
          <UserTagPicker
            onSelect={onAddParticipant}
            selectedUserIds={selectedUserIds}
            currentUserId={currentUserId}
            buttonLabel={
              <span className="flex items-center gap-1">
                <Plus className="h-3 w-3" />
                {otherParticipants.length === 0 ? "Tag someone" : "Add"}
              </span>
            }
            className="h-8 rounded-full border-dashed"
          />
        )}
      </div>
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────────────
// MODE TOGGLE (Singles/Doubles)
// ──────────────────────────────────────────────────────────────────────────────

type ModeToggleProps<T extends string> = {
  label: string;
  options: { value: T; label: string }[];
  value: T;
  onChange: (value: T) => void;
};

export function ModeToggle<T extends string>({
  label,
  options,
  value,
  onChange,
}: ModeToggleProps<T>) {
  return (
    <div className="space-y-2">
      <span className="text-sm font-medium text-text-primary">{label}</span>
      <div className="flex gap-2">
        {options.map((option) => (
          <Button
            key={option.value}
            type="button"
            variant={value === option.value ? "default" : "outline"}
            size="sm"
            onClick={() => onChange(option.value)}
            className={cn(
              value === option.value && "bg-brand text-text-inverse",
            )}
          >
            {option.label}
          </Button>
        ))}
      </div>
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────────────
// VALIDATION WARNING
// ──────────────────────────────────────────────────────────────────────────────

type ValidationWarningProps = {
  message: string;
};

export function ValidationWarning({ message }: ValidationWarningProps) {
  return (
    <div className="rounded-lg border border-warning/50 bg-warning/10 px-3 py-2 text-sm text-warning">
      {message}
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────────────
// UTILITY: Convert UserTagSuggestion to Participant
// ──────────────────────────────────────────────────────────────────────────────

export function suggestionToParticipant(
  suggestion: UserTagSuggestion,
): Participant {
  return {
    id: suggestion.id,
    displayName: suggestion.label,
    avatarUrl: suggestion.avatarUrl,
    handle: suggestion.handle,
  };
}
